import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { alphabet, colors, animals, numbers } from '../data/arabic';

interface Props { onBack: () => void; age: 'little' | 'big'; }

const pool = [
  ...alphabet.slice(0, 10).map(a => ({ question: `What letter is this? ${a.letter}`, answer: a.name, options: [a.name, alphabet[5].name, alphabet[10].name, alphabet[15].name], emoji: '🔤' })),
  ...colors.map(c => ({ question: `What color is ${c.emoji}?`, answer: c.english, options: [c.english, colors[1].english, colors[3].english, colors[6].english], emoji: c.emoji })),
  ...animals.map(a => ({ question: `What animal is ${a.emoji}?`, answer: a.english, options: [a.english, animals[1].english, animals[3].english, animals[5].english], emoji: a.emoji })),
];

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export const Quiz: React.FC<Props> = ({ onBack, age }) => {
  const [questions] = useState(() => shuffle(pool).slice(0, age === 'little' ? 5 : 8).map(q => ({ ...q, options: shuffle(Array.from(new Set(q.options)).slice(0, 4)) })));
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);

  const q = questions[current];

  const handleAnswer = useCallback((opt: string) => {
    if (selected) return;
    setSelected(opt);
    if (opt === q.answer) setScore(s => s + 1);
    setTimeout(() => {
      if (current + 1 >= questions.length) setDone(true);
      else { setCurrent(c => c + 1); setSelected(null); }
    }, 1200);
  }, [selected, q, current, questions.length]);

  if (done) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', textAlign: 'center', padding: '20px' }}>
      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring' }}>
        <div style={{ fontSize: '100px' }}>{score === questions.length ? '🏆' : score >= questions.length / 2 ? '⭐' : '💪'}</div>
        <h2 style={{ fontFamily: "'Fredoka One', cursive", fontSize: '3rem', color: 'var(--primary)', margin: '20px 0' }}>
          {score === questions.length ? 'Perfect!' : score >= questions.length / 2 ? 'Great job!' : 'Keep practicing!'}
        </h2>
        <p style={{ fontSize: '1.5rem', color: 'var(--text)', marginBottom: '30px' }}>
          You got <strong>{score}</strong> out of <strong>{questions.length}</strong>! 🎉
        </p>
        <motion.button whileTap={{ scale: 0.9 }} onClick={onBack}
          style={{ background: '#FF6B6B', border: 'none', borderRadius: '50px', padding: '15px 40px', color: 'white', fontFamily: "'Fredoka One', cursive", fontSize: '1.3rem', cursor: 'pointer' }}>
          Back to Menu 🏠
        </motion.button>
      </motion.div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', padding: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ width: '100%', maxWidth: '600px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
          <motion.button whileTap={{ scale: 0.9 }} onClick={onBack}
            style={{ background: 'white', border: '3px solid #6C63FF', borderRadius: '50px', padding: '10px 20px', cursor: 'pointer', color: '#6C63FF', fontWeight: 700 }}>
            ← Back
          </motion.button>
          <h2 style={{ fontFamily: "'Fredoka One', cursive", color: '#6C63FF', fontSize: '1.8rem' }}>Quiz Time! 🎯</h2>
          <div style={{ marginLeft: 'auto', fontWeight: 700, color: '#6C63FF' }}>⭐ {score}</div>
        </div>

        <div style={{ background: '#F0F0F0', borderRadius: '20px', height: '12px', marginBottom: '30px', overflow: 'hidden' }}>
          <motion.div animate={{ width: `${((current + 1) / questions.length) * 100}%` }}
            style={{ height: '100%', background: 'linear-gradient(90deg, #6C63FF, #FF6B6B)', borderRadius: '20px' }} />
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={current} initial={{ x: 100, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: -100, opacity: 0 }}
            style={{ background: 'white', borderRadius: '24px', padding: '40px 30px', textAlign: 'center', boxShadow: 'var(--shadow)', marginBottom: '20px' }}>
            <div style={{ fontSize: '60px', marginBottom: '15px' }}>{q.emoji}</div>
            <p style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--text)' }}>{q.question}</p>
          </motion.div>
        </AnimatePresence>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          {q.options.map((opt, i) => {
            const isCorrect = opt === q.answer;
            const isSelected = opt === selected;
            let bg = 'white'; let border = '#DDD';
            if (selected) { if (isCorrect) { bg = '#00B894'; border = '#00B894'; } else if (isSelected) { bg = '#FF7675'; border = '#FF7675'; } }
            return (
              <motion.button key={i} whileHover={!selected ? { scale: 1.04 } : {}} whileTap={!selected ? { scale: 0.96 } : {}}
                onClick={() => handleAnswer(opt)}
                style={{ background: bg, border: `3px solid ${border}`, borderRadius: '16px', padding: '18px 15px', cursor: selected ? 'default' : 'pointer', fontSize: '1.1rem', fontWeight: 700, color: selected && (isCorrect || isSelected) ? 'white' : 'var(--text)', transition: 'all 0.2s' }}>
                {['A', 'B', 'C', 'D'][i]}. {opt}
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
